import * as icons from '@navikt/aksel-icons';
import { TFunction } from 'i18next';
import { Routes } from '../enums/Routes.ts';
import { iconMap } from '../components/Sidebar/iconMap';
import type { TenantFeatureKey } from '../api/tenantFeatures';
import { TeamLeaderRolesUtils } from './RolesUtils/TeamLeaderRoles.utils';

export type AkselIcons = keyof typeof icons;
export type AvailableIcons = keyof typeof iconMap;

/** Hovedgrupper i navigasjonen. Hver gruppe hører til en rolle, utenom «Mitt ansvar», som er brukerens eget. */
export type NavigationGroupKey = 'teamleder' | 'hr' | 'verneombud' | 'dagligleder' | 'admin' | 'mine';

export type SideBarRouteItem = {
  route: Routes;
  icon: AvailableIcons;
  label: string;
  /** Hovedgruppe, eller 'account' for kontolenker nederst i menyen */
  group: NavigationGroupKey | 'account';
  allowedRoles?: string[];
  hideRoute?: boolean;
  requiredFeature?: TenantFeatureKey;
  /** Only marked as the current page on its own path, not on the pages under it */
  end?: boolean;
  /** Pages under this one, shown as a sub-menu while one of them is open */
  children?: Array<{ route: Routes; label: string }>;
};

export type NavigationGroup = {
  key: NavigationGroupKey;
  label: string;
  routes: SideBarRouteItem[];
};

export type Navigation = {
  groups: NavigationGroup[];
  accountRoutes: SideBarRouteItem[];
};

const GROUP_ORDER: NavigationGroupKey[] = ['teamleder', 'hr', 'verneombud', 'dagligleder', 'admin', 'mine'];

export class SideBarRoutes {
  public static getRoutes(t?: TFunction): Array<SideBarRouteItem> {
    return ([
      // Teamleder – den som leder et team, og organisasjonens administrator
      {
        route: Routes.Dashboard,
        icon: 'TasklistIcon',
        label: t ? t('sidebar.routes.overview') : 'Oversikt',
        group: 'teamleder',
      },
      {
        route: Routes.Surveys,
        icon: 'FileTextIcon',
        label: t ? t('sidebar.routes.surveys') : 'Undersøkelser',
        group: 'teamleder',
      },
      {
        route: Routes.Teams,
        icon: 'PersonGroupIcon',
        label: t ? t('sidebar.routes.myTeams') : 'Mine team',
        group: 'teamleder',
      },
      {
        route: Routes.ContentManagementSystemArticles,
        icon: 'NewspaperIcon',
        label: t ? t('sidebar.routes.situationalHelp') : 'Situasjonshjelp',
        group: 'teamleder',
        hideRoute: !window.localStorage.getItem('shouldShowCms'),
      },
      // HR – HR_ADMIN
      {
        route: Routes.HRHomeFullPath,
        icon: 'HouseIcon',
        label: t ? t('sidebar.routes.home') : 'Hjem',
        allowedRoles: ['HR_ADMIN'],
        group: 'hr',
      },
      {
        route: Routes.HRWorkEnvironmentFullPath,
        icon: 'HeartIcon',
        label: t ? t('sidebar.routes.workEnvironment') : 'Arbeidsmiljø',
        allowedRoles: ['HR_ADMIN'],
        group: 'hr',
        requiredFeature: 'WORK_ENVIRONMENT',
        children: [
          {
            route: Routes.HRWorkEnvironmentFullPath,
            label: t ? t('sidebar.routes.workEnvironmentOverview') : 'Oversikt',
          },
          {
            route: Routes.HRSafetyRepsFullPath,
            label: t ? t('sidebar.routes.safetyReps') : 'Verneombud',
          },
          {
            route: Routes.HRRiskMatrixFullPath,
            label: t ? t('sidebar.routes.riskMatrix') : 'Risikomatrise',
          },
          {
            route: Routes.HRRiskFactorsFullPath,
            label: t ? t('sidebar.routes.riskFactors') : 'Risikofaktorer og spørsmål',
          },
        ],
      },
      {
        route: Routes.HRSafetyRoundOverviewFullPath,
        icon: 'TasklistIcon',
        label: t ? t('sidebar.routes.safetyRounds') : 'Vernerunder',
        allowedRoles: ['HR_ADMIN'],
        group: 'hr',
        requiredFeature: 'SAFETY_ROUNDS',
        children: [
          {
            route: Routes.HRSafetyRoundOverviewFullPath,
            label: t ? t('sidebar.routes.safetyRoundOverview') : 'Oversikt og logg',
          },
          {
            route: Routes.HRSafetyRoundChecklistsFullPath,
            label: t ? t('sidebar.routes.safetyRoundChecklists') : 'Sjekklistemaler',
          },
        ],
      },
      {
        route: Routes.HRPsychologicalSafetyFullPath,
        icon: 'ShieldLockIcon',
        label: t ? t('sidebar.routes.teamDynamics') : 'Psykologisk trygghet',
        allowedRoles: ['HR_ADMIN'],
        group: 'hr',
      },
      {
        route: Routes.CustomSurveyTemplates,
        icon: 'BarChartIcon',
        label: t ? t('sidebar.routes.customSurveys') : 'Pulsmålinger',
        allowedRoles: ['HR_ADMIN'],
        group: 'hr',
        requiredFeature: 'CUSTOM_SURVEYS',
      },
      // Verneombud – SAFETY_REP
      {
        route: Routes.SafetyRepOverview,
        icon: 'HouseIcon',
        label: t ? t('sidebar.routes.safetyRepOverview') : 'Oversikt',
        allowedRoles: ['SAFETY_REP'],
        group: 'verneombud',
        end: true,
      },
      {
        route: Routes.SafetyRepTeams,
        icon: 'PersonGroupIcon',
        label: t ? t('sidebar.routes.safetyRepTeams') : 'Teamene mine',
        allowedRoles: ['SAFETY_REP'],
        group: 'verneombud',
      },
      {
        route: Routes.SafetyRepSigning,
        icon: 'FileTextIcon',
        label: t ? t('sidebar.routes.safetyRepSigning') : 'Mine oppgaver',
        allowedRoles: ['SAFETY_REP'],
        group: 'verneombud',
      },
      {
        route: Routes.SafetyRepRoundPlans,
        icon: 'TasklistIcon',
        label: t ? t('sidebar.routes.safetyRounds') : 'Vernerunder',
        allowedRoles: ['SAFETY_REP'],
        group: 'verneombud',
        requiredFeature: 'SAFETY_ROUNDS',
        children: [
          {
            route: Routes.SafetyRepRoundPlans,
            label: t ? t('sidebar.routes.safetyRoundPlans') : 'Planer og runder',
          },
          {
            route: Routes.SafetyRoundOverview,
            label: t ? t('sidebar.routes.safetyRoundOverview') : 'Oversikt og logg',
          },
          {
            route: Routes.SafetyRoundNotifications,
            label: t ? t('sidebar.routes.safetyRoundNotifications') : 'Velg e-poster',
          },
        ],
      },
      // Daglig leder – GENERAL_MANAGER
      {
        route: Routes.GeneralManagerTasks,
        icon: 'TasklistIcon',
        label: t ? t('sidebar.routes.generalManagerTasks') : 'Mine oppgaver',
        allowedRoles: ['GENERAL_MANAGER'],
        group: 'dagligleder',
        requiredFeature: 'SAFETY_ROUNDS',
      },
      {
        route: Routes.GeneralManagerSafetyRoundOverview,
        icon: 'BarChartIcon',
        label: t ? t('sidebar.routes.safetyRoundOverview') : 'Oversikt og logg',
        allowedRoles: ['GENERAL_MANAGER'],
        group: 'dagligleder',
        requiredFeature: 'SAFETY_ROUNDS',
      },
      // Administrasjon – ORG_ADMIN, PARTNER_ADMIN, SUPER_ADMIN
      {
        route: Routes.OrganizationSettings,
        icon: 'Buildings2Icon',
        label: t ? t('sidebar.routes.organization') : 'Organisasjon',
        allowedRoles: ['ORG_ADMIN'],
        group: 'admin',
      },
      {
        route: Routes.PartnerAdminDashboard,
        icon: 'HandshakeIcon',
        label: t ? t('sidebar.routes.partnerPortal') : 'Partnerportal',
        allowedRoles: ['PARTNER_ADMIN'],
        group: 'admin',
      },
      {
        route: Routes.SystemAdmin,
        icon: 'ShieldIcon',
        label: t ? t('sidebar.routes.systemAdmin') : 'Systemeier',
        allowedRoles: ['SUPER_ADMIN'],
        group: 'admin',
      },
      // Mitt ansvar – alle innloggede. Avvik kan bli lagt på hvem som helst, også den som ikke leder et team.
      {
        route: Routes.MyDeviations,
        icon: 'TasklistIcon',
        label: t ? t('sidebar.routes.myDeviations') : 'Mine avvik',
        group: 'mine',
        requiredFeature: 'SAFETY_ROUNDS',
      },
      // Min konto – alle innloggede
      {
        route: Routes.Settings,
        icon: 'CogIcon',
        label: t ? t('sidebar.routes.settings') : 'Innstillinger',
        group: 'account',
      },
    ] satisfies Array<SideBarRouteItem>).filter((route) => {
      return !route.hideRoute;
    });
  }

  public static get routes(): Array<SideBarRouteItem> {
    return SideBarRoutes.getRoutes();
  }

  public static filterRoutesByRole(
    routes: Array<SideBarRouteItem>,
    userRoles: string[] = [],
  ): Array<SideBarRouteItem> {
    return routes.filter((route) => {
      if (route.allowedRoles && route.allowedRoles.length > 0) {
        return route.allowedRoles.some((role) => userRoles.includes(role));
      }

      return true;
    });
  }

  public static filterRoutesByFeatures(
    routes: Array<SideBarRouteItem>,
    enabledFeatures: TenantFeatureKey[] = [],
  ): Array<SideBarRouteItem> {
    return routes.filter((route) => {
      if (route.requiredFeature) {
        return enabledFeatures.includes(route.requiredFeature);
      }

      return true;
    });
  }

  /** Where the organisation does not use safety representatives, their area and HR's page for them are left out */
  public static withoutSafetyReps(routes: Array<SideBarRouteItem>): Array<SideBarRouteItem> {
    return routes
      .filter((route) => route.group !== 'verneombud')
      .map((route) =>
        route.children
          ? { ...route, children: route.children.filter((child) => child.route !== Routes.HRSafetyRepsFullPath) }
          : route,
      );
  }

  /**
   * Teamlederens del av menyen er for den som leder et team, og for administratoren som setter opp teamene. HR,
   * verneombud og medlemmer som ikke leder et team, får den ikke: dashbordet ville vist et team de ikke leder.
   */
  public static withoutTeamLeaderUnlessLeading(
    routes: Array<SideBarRouteItem>,
    userRoles: string[],
    leadsTeam: boolean,
  ): Array<SideBarRouteItem> {
    return TeamLeaderRolesUtils.hasTeamLeaderPart(userRoles, leadsTeam)
      ? routes
      : routes.filter((route) => route.group !== 'teamleder');
  }

  /**
   * «Oversikt og logg» for vernerunder vises ett sted, der den viser mest: hos HR, ellers hos daglig leder, ellers hos
   * verneombudet. Samme side to steder i menyen forvirrer.
   */
  public static withOneSafetyRoundOverview(
    routes: Array<SideBarRouteItem>,
    userRoles: string[],
  ): Array<SideBarRouteItem> {
    const isHr = userRoles.includes('HR_ADMIN');
    const isManager = userRoles.includes('GENERAL_MANAGER');
    return routes
      .filter((route) => !(isHr && route.route === Routes.GeneralManagerSafetyRoundOverview))
      .map((route) =>
        (isHr || isManager) && route.group === 'verneombud' && route.children
          ? { ...route, children: route.children.filter((child) => child.route !== Routes.SafetyRoundOverview) }
          : route,
      );
  }

  /**
   * Gruppen som hører til brukerens hovedrolle vises først.
   */
  public static getPrimaryGroup(userRoles: string[] = []): NavigationGroupKey {
    if (userRoles.includes('HR_ADMIN')) {
      return 'hr';
    }
    if (userRoles.includes('ORG_ADMIN')) {
      return 'admin';
    }
    if (userRoles.includes('SAFETY_REP')) {
      return 'verneombud';
    }
    if (userRoles.includes('GENERAL_MANAGER')) {
      return 'dagligleder';
    }
    return 'teamleder';
  }

  /**
   * Bygger navigasjonen for brukeren: rollefiltrerte, ikke-tomme hovedgrupper
   * sortert etter hovedrolle, og kontolenker for seg.
   */
  public static getNavigation(
    userRoles: string[] = [],
    enabledFeatures: TenantFeatureKey[] = [],
    t?: TFunction,
    usesSafetyReps = true,
    /** The group of the role the user chose in onboarding, which then comes first */
    preferredGroup: NavigationGroupKey | null = null,
    /** Leads at least one team. Unknown counts as not leading, so the team leader's part is never shown by mistake. */
    leadsTeam = false,
  ): Navigation {
    const permitted = SideBarRoutes.withoutTeamLeaderUnlessLeading(
      SideBarRoutes.filterRoutesByFeatures(
        SideBarRoutes.filterRoutesByRole(SideBarRoutes.getRoutes(t), userRoles),
        enabledFeatures,
      ),
      userRoles,
      leadsTeam,
    );
    const routes = SideBarRoutes.withOneSafetyRoundOverview(
      usesSafetyReps ? permitted : SideBarRoutes.withoutSafetyReps(permitted),
      userRoles,
    );
    const primaryGroup = preferredGroup ?? SideBarRoutes.getPrimaryGroup(userRoles);
    const orderedKeys = [primaryGroup, ...GROUP_ORDER.filter((key) => key !== primaryGroup)];

    const groups = orderedKeys
      .map((key) => ({
        key,
        label: SideBarRoutes.getGroupLabel(key, t),
        routes: routes.filter((route) => route.group === key),
      }))
      .filter((group) => group.routes.length > 0);

    return {
      groups,
      accountRoutes: routes.filter((route) => route.group === 'account'),
    };
  }

  public static getGroupLabel(group: NavigationGroupKey, t?: TFunction): string {
    if (t) {
      const labels: Record<NavigationGroupKey, string> = {
        teamleder: t('sidebar.groups.teamLeader'),
        hr: t('sidebar.groups.hr'),
        verneombud: t('sidebar.groups.safetyRep'),
        dagligleder: t('sidebar.groups.generalManager'),
        admin: t('sidebar.groups.admin'),
        mine: t('sidebar.groups.mine'),
      };
      return labels[group] ?? '';
    }

    const labels: Record<NavigationGroupKey, string> = {
      teamleder: 'Teamleder',
      hr: 'HR',
      verneombud: 'Verneombud',
      dagligleder: 'Daglig leder',
      admin: 'Administrasjon',
      mine: 'Mitt ansvar',
    };
    return labels[group] ?? '';
  }
}
